import React from 'react';
import styled from 'styled-components';

const StyledServicesSection = styled.section`
  margin: 150px 0;
  padding: 100px 50px;
  background: linear-gradient(135deg, rgba(0, 153, 255, 0.03) 0%, rgba(0, 153, 255, 0) 100%);
  border-radius: 8px;

  @media (max-width: 768px) {
    margin: 100px 0;
    padding: 60px 30px;
  }

  @media (max-width: 480px) {
    margin: 60px 0;
    padding: 40px 20px;
  }

  .section-header {
    text-align: center;
    margin-bottom: 80px;

    .accent-line {
      font-size: var(--fz-sm);
      letter-spacing: 0.15em;
      color: var(--green);
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 20px;
    }

    h2 {
      font-size: clamp(32px, 8vw, 48px);
      font-weight: 700;
      color: var(--lightest-slate);
      margin-bottom: 20px;
      line-height: 1.2;
    }

    p {
      max-width: 600px;
      margin: 0 auto;
      font-size: var(--fz-lg);
      color: var(--slate);
      line-height: 1.8;

      @media (max-width: 768px) {
        font-size: var(--fz-md);
      }
    }
  }

  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 40px;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }
`;

const ServiceCard = styled.div`
  padding: 40px;
  background-color: rgba(17, 34, 51, 0.8);
  border: 1px solid rgba(100, 200, 100, 0.2);
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 0;
    background-color: var(--green);
    transition: height 0.3s ease;
  }

  &:hover {
    border-color: var(--green);
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(100, 200, 100, 0.1);

    &::before {
      height: 100%;
    }

    h3 {
      color: var(--green);
    }
  }

  h3 {
    font-size: var(--fz-lg);
    color: var(--lightest-slate);
    margin-bottom: 16px;
    margin-top: 0;
    transition: color 0.3s ease;
    font-weight: 600;
  }

  p {
    font-size: var(--fz-sm);
    color: var(--light-slate);
    line-height: 1.6;
    margin: 0;
  }

  ul {
    list-style: none;
    padding: 0;
    margin-top: 16px;

    li {
      font-size: var(--fz-xs);
      color: var(--light-slate);
      margin-bottom: 8px;
      padding-left: 20px;
      position: relative;

      &:before {
        content: '▸';
        position: absolute;
        left: 0;
        color: var(--green);
      }
    }
  }
`;

const Services = () => {
  const services = [
    {
      title: 'Data-Driven Solutions',
      description:
        'Transform your business with data engineering, analytics, and data governance expertise.',
      skills: ['Data Engineers', 'Data Analysts', 'DataOps', 'Analytics', 'Data Governance'],
    },
    {
      title: 'Cloud & Infrastructure',
      description:
        'Design and deploy robust cloud infrastructure with DevOps and security best practices.',
      skills: ['Cloud Engineers', 'DevOps', 'DevSecOps', 'Architects', 'Site Reliability'],
    },
    {
      title: 'AI & Machine Learning',
      description:
        'Leverage cutting-edge AI technology and machine learning for intelligent automation.',
      skills: ['Machine Learning', 'Data Scientists', 'AIOps', 'MLOps', 'Generative AI'],
    },
    {
      title: 'Other IT Services',
      description:
        'Full-stack development, product management, and specialized IT roles for diverse industries.',
      skills: ['Product Managers', 'UI/UX Designers', 'Full Stack Developers', 'IoT', 'FinTech'],
    },
  ];

  return (
    <StyledServicesSection id="services">
      <div className="section-header">
        <div className="accent-line">What We Provide</div>
        <h2>Comprehensive IT Solutions & Expert Services</h2>
        <p>
          From data-driven insights to cutting-edge AI integration, delivering impactful solutions
          that empower business transformation and innovation.
        </p>
      </div>

      <div className="services-grid">
        {services.map((service, index) => (
          <ServiceCard key={index}>
            <h3>{service.title}</h3>
            <p>{service.description}</p>
            <ul>
              {service.skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </ServiceCard>
        ))}
      </div>
    </StyledServicesSection>
  );
};

export default Services;
